import { IonIcon } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { documentsOutline, helpCircleOutline } from "ionicons/icons";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { EntryStatusChip } from "@/components/molecules/shift/entry-status-chip";
import type { ExpenseClaim } from "@/lib/backend/api";
import { xMarkIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { toEntryState } from "@/lib/shift/entry-status";

interface DeclarationPreviewModalProps {
  modalRef: React.RefObject<HTMLIonModalElement>;
  infoModalRef: React.RefObject<HTMLIonModalElement>;
  expenseClaim: ExpenseClaim | null | undefined;
  expenseClaimFileUrl: string | null | undefined;
  isLoadingExpenseClaim: boolean;
  isErrorExpenseClaim: boolean;
  closeModal: () => void;
}

export function DeclarationPreviewModal({
  modalRef,
  infoModalRef,
  expenseClaim,
  expenseClaimFileUrl,
  isLoadingExpenseClaim,
  isErrorExpenseClaim,
  closeModal,
}: DeclarationPreviewModalProps) {
  const { t } = useTranslation();
  const expensesState = toEntryState(expenseClaim?.status);

  return (
    <ModalSheetAutoHeight ref={modalRef} onDidDismiss={closeModal}>
      <div className="flex flex-col gap-5 px-6 pb-6 pt-4">
        {/* Header with status badge and info icon */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold leading-[1.6] text-txt-primary">
              {t("shift:declaration.modal.title")}
            </h2>
            <button
              type="button"
              onClick={() => infoModalRef.current?.present()}
              className="flex size-12 items-center justify-center"
            >
              <IonIcon icon={helpCircleOutline} className="text-2xl text-txt-tertiary" />
            </button>
          </div>

          {/* Was an unguarded `t()` on the raw status, so a claim without one printed the
              translation key. The chip renders nothing it has no word for. */}
          {expensesState && (
            <div className="flex flex-col gap-1.5">
              <EntryStatusChip kind="expenses" status={expenseClaim?.status} />
              <p className="text-sm leading-[1.5] text-txt-secondary">
                {t(`shift:entry-status.hint.${expensesState}`)}
              </p>
            </div>
          )}
        </div>

        {/* Loading state */}
        {isLoadingExpenseClaim && (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-bordercolor-brand border-t-transparent" />
            <p className="mt-4 text-sm text-txt-secondary">{t("shift:declaration.loading")}</p>
          </div>
        )}

        {/* Error state */}
        {isErrorExpenseClaim && !expenseClaim && (
          <div className="bg-error-100/10 flex flex-col items-center justify-center gap-3 rounded-xl p-6">
            {/* Standalone 36px glyph, no text: WCAG 1.4.11 asks 3:1 and `error-100` clears it on
                this wash (3.29:1) for every brand. Keeping the flat brand red here. */}
            {/* eslint-disable-next-line no-restricted-syntax */}
            <IonIcon icon={xMarkIcon} className="text-4xl text-error-100" />
            <p className="text-center text-sm text-txt-secondary">
              {t("shift:declaration.error.fetch")}
            </p>
          </div>
        )}

        {/* Content - tightly grouped fields */}
        {expenseClaim && !isLoadingExpenseClaim && (
          <>
            <div className="flex flex-col gap-2">
              {/* Type */}
              <div className="flex flex-col gap-1">
                <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
                  {t("shift:declaration.fields.type")}
                </p>
                <p className="text-base font-medium leading-[1.5] text-txt-primary">
                  {expenseClaim.type === "CAR"
                    ? t("fields:travelExpenses.type.options.car")
                    : expenseClaim.type === "PUBLIC_TRANSPORT"
                      ? t("fields:travelExpenses.type.options.public-transport")
                      : t("fields:travelExpenses.type.options.other")}
                </p>
              </div>

              {/* Kilometers (only for km declarations). The euro amount below is server-computed
                  from the km-rate; euro-only claims have no kilometers and render as before. */}
              {expenseClaim.kilometers != null && (
                <div className="flex flex-col gap-1">
                  <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
                    {t("shift:declaration.fields.kilometers")}
                  </p>
                  <p className="text-base font-medium leading-[1.5] text-txt-primary">
                    {parseFloat(expenseClaim.kilometers)} km
                  </p>
                </div>
              )}

              {/* Cost */}
              <div className="flex flex-col gap-1">
                <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
                  {t("shift:declaration.fields.cost")}
                </p>
                <p className="text-base font-medium leading-[1.5] text-txt-primary">
                  € {expenseClaim.amount ? parseFloat(expenseClaim.amount).toFixed(2) : "0.00"}
                </p>
              </div>

              {/* Note */}
              {expenseClaim.note && typeof expenseClaim.note === "string" && (
                <div className="flex flex-col gap-1">
                  <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
                    {t("shift:declaration.fields.note")}
                  </p>
                  <p className="text-base font-medium leading-[1.5] text-txt-primary">
                    {expenseClaim.note}
                  </p>
                </div>
              )}

              {/* Document Preview */}
              {expenseClaimFileUrl && (
                <div className="flex flex-col gap-1">
                  <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
                    {t("shift:declaration.fields.attachment")}
                  </p>
                  <div className="overflow-hidden rounded-xl border border-solid border-bordercolor-primary">
                    {expenseClaimFileUrl.includes(".pdf") ||
                    expenseClaimFileUrl.includes("expense-claim.pdf") ? (
                      <a
                        href={expenseClaimFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-4 transition-colors hover:bg-surface-secondary"
                      >
                        <IonIcon icon={documentsOutline} className="size-8 text-txt-secondary" />
                        <div className="flex flex-1 flex-col">
                          <p className="text-base font-medium text-txt-primary">PDF Document</p>
                          <p className="text-sm text-txt-tertiary">Klik om te openen</p>
                        </div>
                      </a>
                    ) : (
                      <a
                        href={expenseClaimFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={expenseClaimFileUrl}
                          alt="Declaratie bijlage"
                          className="h-auto max-h-[400px] w-full object-contain"
                        />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* All attachments (names + optional descriptions) */}
              {expenseClaim.attachments && expenseClaim.attachments.length > 0 && (
                <div className="flex flex-col gap-1">
                  <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
                    {`Bijlagen (${expenseClaim.attachments.length})`}
                  </p>
                  <ul className="flex flex-col gap-1">
                    {expenseClaim.attachments.map((att) => (
                      <li
                        key={att.id}
                        className="flex items-center gap-2 rounded-lg border border-solid border-bordercolor-primary px-3 py-2"
                      >
                        <IonIcon
                          icon={documentsOutline}
                          className="size-5 shrink-0 text-txt-secondary"
                        />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm font-medium text-txt-primary">
                            {att.name}
                          </span>
                          {att.description && (
                            <span className="truncate text-xs text-txt-tertiary">
                              {att.description}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={() => modalRef.current?.dismiss()}
              className="w-full rounded-xl border border-solid border-bordercolor-primary py-3 text-center text-base font-semibold tracking-[0.08px] text-txt-primary"
            >
              {t("common:close")}
            </button>
          </>
        )}
      </div>
    </ModalSheetAutoHeight>
  );
}
